/**
 * Inquiry Analysis Utilities - Five S Smart Analysis
 *
 * Strict bureau-aware legitimacy matching:
 * - LIKELY LEGITIMATE: inquiry linked to a matching open revolving account on the same bureau
 * - NEEDS REVIEW: no strong linkage evidence
 * - POSSIBLE DUPLICATE: duplicate inquiries detected
 */

export const INQUIRY_RECOMMENDATION = {
  LIKELY_LEGITIMATE: 'LIKELY_LEGITIMATE',
  NEEDS_REVIEW: 'NEEDS_REVIEW',
  POSSIBLE_DUPLICATE: 'POSSIBLE_DUPLICATE',
  INCONSISTENT: 'INCONSISTENT',
  USER_CONFIRMATION_NEEDED: 'USER_CONFIRMATION_NEEDED',
};

export const RECOMMENDATION_LABELS = {
  [INQUIRY_RECOMMENDATION.LIKELY_LEGITIMATE]: 'Likely Legitimate',
  [INQUIRY_RECOMMENDATION.NEEDS_REVIEW]: 'Needs Review',
  [INQUIRY_RECOMMENDATION.POSSIBLE_DUPLICATE]: 'Possible Duplicate',
  [INQUIRY_RECOMMENDATION.INCONSISTENT]: 'Inconsistent',
  [INQUIRY_RECOMMENDATION.USER_CONFIRMATION_NEEDED]: 'Confirmation Needed',
};

export const RECOMMENDATION_COLORS = {
  [INQUIRY_RECOMMENDATION.LIKELY_LEGITIMATE]: {
    bg: 'rgba(15,172,108,0.12)',
    color: '#0D9F64',
    border: 'rgba(15,172,108,0.2)',
  },
  [INQUIRY_RECOMMENDATION.NEEDS_REVIEW]: {
    bg: 'rgba(234,178,58,0.16)',
    color: '#EAB23A',
    border: 'rgba(234,178,58,0.2)',
  },
  [INQUIRY_RECOMMENDATION.POSSIBLE_DUPLICATE]: {
    bg: 'rgba(147,51,234,0.12)',
    color: '#9333ea',
    border: 'rgba(147,51,234,0.2)',
  },
  [INQUIRY_RECOMMENDATION.INCONSISTENT]: {
    bg: 'rgba(239,68,68,0.12)',
    color: '#ef4444',
    border: 'rgba(239,68,68,0.2)',
  },
  [INQUIRY_RECOMMENDATION.USER_CONFIRMATION_NEEDED]: {
    bg: 'rgba(59,130,246,0.12)',
    color: '#3b82f6',
    border: 'rgba(59,130,246,0.2)',
  },
};

export const INQUIRY_LINK_DAYS_MIN = 0;
export const INQUIRY_LINK_DAYS_MAX = 45;

const ISSUER_ALIASES = {
  'bank of america': ['bofa', 'bankofamerica', 'boa', 'bank america', 'bk of amer'],
  'capital one': ['capitalone', 'cap one', 'capital one bank'],
  'jpmorgan chase': ['jpmcb', 'chase', 'jp morgan', 'jpmcb card'],
  synchrony: ['syncb', 'synchrony bank', 'syncb/sams', 'sams club'],
  discover: ['discover bank', 'discover financial'],
  citibank: ['citi', 'citicorp', 'citigroup'],
  'wells fargo': ['wellsfargo', 'wf bank'],
  barclays: ['barclaycard', 'webna card'],
  'american express': ['amex', 'americanexpress'],
};

export const normalizeCreditorName = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[.,\-/\\()'"]/g, ' ')
    .replace(/\s+(inc|llc|corp|corporation|company|co|bank|na|n\.a\.|financial|services|credit|card|auto|lending|mortgage)\.?$/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const getCanonicalName = (name) => {
  const normalized = normalizeCreditorName(name);
  for (const [canonical, aliases] of Object.entries(ISSUER_ALIASES)) {
    if (normalized === canonical) return canonical;
    for (const alias of aliases) {
      if (normalized.includes(alias) || alias.includes(normalized)) {
        return canonical;
      }
    }
  }
  return normalized;
};

export const creditorNamesMatch = (inquiryName, accountName) => {
  const normalizedInquiry = normalizeCreditorName(inquiryName);
  const normalizedAccount = normalizeCreditorName(accountName);
  if (!normalizedInquiry || !normalizedAccount) return false;
  if (normalizedInquiry === normalizedAccount) return true;
  if (getCanonicalName(inquiryName) === getCanonicalName(accountName)) return true;
  if (normalizedInquiry.length >= 4 && normalizedAccount.includes(normalizedInquiry)) return true;
  if (normalizedAccount.length >= 4 && normalizedInquiry.includes(normalizedAccount)) return true;
  const inquiryWords = normalizedInquiry.split(' ').filter((w) => w.length >= 3);
  const accountWords = normalizedAccount.split(' ').filter((w) => w.length >= 3);
  return inquiryWords.length > 0 && accountWords.length > 0 && inquiryWords[0] === accountWords[0] && inquiryWords[0].length >= 4;
};

const normalizeBureau = (value) => {
  if (!value) return '';
  const text = String(value).trim().toLowerCase();
  if (text === 'ex' || text.includes('experian')) return 'experian';
  if (text === 'tu' || text.includes('transunion') || text.includes('trans union')) return 'transunion';
  if (text === 'eq' || text.includes('equifax')) return 'equifax';
  return text;
};

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getAccountTypeCategory = (accountType) => {
  const type = (accountType || '').toLowerCase();
  if (type.includes('revolving') || type.includes('credit card') || type.includes('charge') || type.includes('card')) {
    return 'credit_card';
  }
  if (type.includes('mortgage') || type.includes('home loan')) return 'mortgage';
  if (type.includes('auto') || type.includes('car') || type.includes('vehicle')) return 'auto';
  if (type.includes('installment') || type.includes('loan') || type.includes('personal')) return 'installment';
  return 'other';
};

const getInquiryTypeCategory = (inquiry) => {
  const type = (inquiry.type || inquiry.business_type || inquiry.inquiry_type || '').toLowerCase();
  if (type.includes('credit card') || type.includes('bank credit') || type.includes('revolving') || type.includes('card')) {
    return 'credit_card';
  }
  if (type.includes('mortgage') || type.includes('real estate')) return 'mortgage';
  if (type.includes('auto') || type.includes('car') || type.includes('vehicle') || type.includes('dealer')) return 'auto';
  if (type.includes('loan') || type.includes('finance') || type.includes('lending')) return 'installment';
  return 'other';
};

const accountTypeMatchesInquiry = (inquiry, account) => {
  const inquiryType = getInquiryTypeCategory(inquiry);
  const accountType = getAccountTypeCategory(account.account_type || account.account_type_detail || account.type);
  if (inquiryType === accountType) return true;
  return inquiryType === 'other' && accountType === 'credit_card';
};

const isOpenRevolvingAccount = (account) => {
  const status = (account.account_status || account.status || '').toLowerCase();
  const paymentStatus = (account.payment_status || '').toLowerCase();
  const accountType = getAccountTypeCategory(account.account_type || account.account_type_detail || account.type);
  if (accountType !== 'credit_card') return false;
  const isOpen = status.includes('open') || status === 'current';
  const isCurrent = ['current', 'pays as agreed', 'paid as agreed', 'ok', 'never late'].some((token) => paymentStatus.includes(token));
  const isNegative = ['collection', 'chargeoff', 'charge-off', 'derogatory'].some((token) => status.includes(token) || paymentStatus.includes(token));
  return (isOpen || isCurrent) && !isNegative;
};

const accountReportsOnBureau = (account, bureau) => {
  const bureauNorm = normalizeBureau(bureau);
  if (!bureauNorm) return false;
  const reported = [];
  for (const key of ['bureaus_reported', 'bureaus']) {
    const raw = account[key];
    if (Array.isArray(raw)) reported.push(...raw);
    else if (raw) reported.push(raw);
  }
  if (account.bureau) reported.push(account.bureau);
  const normalized = new Set(reported.map((item) => normalizeBureau(item)).filter(Boolean));
  return normalized.has(bureauNorm);
};

export const getInquiryId = (inquiry) => inquiry.item_id || inquiry.inquiry_id || inquiry.id || '';

const getAccountKey = (account) => account.item_id || account.tradeline_id || account.id || `${account.creditor_name}|${account.date_opened}`;

const daysBetweenInquiryAndOpen = (inquiry, account) => {
  const inquiryDate = parseDate(inquiry.inquiry_date || inquiry.date);
  const openDate = parseDate(account.date_opened || account.open_date);
  if (!inquiryDate || !openDate) return null;
  return Math.round((openDate - inquiryDate) / (1000 * 60 * 60 * 24));
};

export const evaluateInquiryAccountLink = (inquiry, account) => {
  const inquiryCreditor = inquiry.company_name || inquiry.creditor_name || inquiry.creditor || inquiry.inquiry_name || '';
  const accountCreditor = account.creditor_name || account.account_name || account.creditor || '';
  const inquiryBureau = inquiry.bureau || inquiry.bureau_code || '';

  if (!creditorNamesMatch(inquiryCreditor, accountCreditor)) return null;
  if (!accountReportsOnBureau(account, inquiryBureau)) return null;
  if (!isOpenRevolvingAccount(account)) return null;
  if (!accountTypeMatchesInquiry(inquiry, account)) return null;

  const daysDiff = daysBetweenInquiryAndOpen(inquiry, account);
  if (daysDiff === null || daysDiff < INQUIRY_LINK_DAYS_MIN || daysDiff > INQUIRY_LINK_DAYS_MAX) return null;

  return {
    inquiry_id: getInquiryId(inquiry),
    account_key: getAccountKey(account),
    creditor_name: accountCreditor || inquiryCreditor,
    account_name: accountCreditor || inquiryCreditor,
    account_type: account.account_type || account.account_type_detail || 'Revolving',
    account_status: account.account_status || account.status || 'Open',
    days_difference: daysDiff,
    inquiry_date: inquiry.inquiry_date || inquiry.date || '',
    bureau: inquiryBureau,
  };
};

export const buildInquiryLegitimacyAssignments = (inquiries = [], accounts = []) => {
  const pairs = [];
  for (const inquiry of inquiries) {
    for (const account of accounts) {
      const link = evaluateInquiryAccountLink(inquiry, account);
      if (link) pairs.push({ daysDiff: link.days_difference, inquiry, account, link });
    }
  }
  pairs.sort((a, b) => a.daysDiff - b.daysDiff);

  const assignments = {};
  const usedInquiries = new Set();
  const usedAccounts = new Set();
  for (const { inquiry, account, link } of pairs) {
    const inquiryId = getInquiryId(inquiry);
    const accountId = getAccountKey(account);
    if (!inquiryId || usedInquiries.has(inquiryId) || usedAccounts.has(accountId)) continue;
    assignments[inquiryId] = link;
    usedInquiries.add(inquiryId);
    usedAccounts.add(accountId);
  }
  return assignments;
};

export const formatInquiryDisplayLabel = (inquiry) => {
  const creditor = inquiry.company_name || inquiry.creditor_name || inquiry.creditor || inquiry.inquiry_name || 'Unknown Inquiry';
  const inquiryDate = inquiry.inquiry_date || inquiry.date || '';
  const bureau = inquiry.bureau || inquiry.bureau_code || '';
  if (inquiryDate && bureau) return `${creditor} · ${bureau} · ${inquiryDate}`;
  if (inquiryDate) return `${creditor} — ${inquiryDate}`;
  return creditor;
};

const buildLegitimateReason = (link) => {
  const creditor = link.creditor_name || link.account_name || 'this creditor';
  return `This inquiry is linked to an open revolving/credit-card account (${creditor}). Disputing legitimate inquiries can harm your credit standing.`;
};

const findDuplicateInquiries = (inquiry, allInquiries) => {
  const inquiryCreditor = normalizeCreditorName(inquiry.company_name || inquiry.creditor || inquiry.creditor_name);
  const inquiryDate = parseDate(inquiry.inquiry_date || inquiry.date);
  if (!inquiryCreditor || !inquiryDate) return [];

  return allInquiries.filter((other) => {
    if (getInquiryId(other) === getInquiryId(inquiry)) return false;
    const otherCreditor = normalizeCreditorName(other.company_name || other.creditor || other.creditor_name);
    const otherDate = parseDate(other.inquiry_date || other.date);
    if (!otherCreditor || !otherDate) return false;
    const daysDiff = Math.abs((inquiryDate - otherDate) / (1000 * 60 * 60 * 24));
    return creditorNamesMatch(inquiryCreditor, otherCreditor) && daysDiff <= 30;
  });
};

const isAccountClosed = (account) => {
  const status = (account.account_status || account.status || '').toLowerCase();
  return status.includes('closed') || status.includes('paid') || status.includes('terminated');
};

export const analyzeInquiry = (inquiry, tradelines = [], allInquiries = [], legitimacyLink = null) => {
  const inquiryCreditor = inquiry.company_name || inquiry.creditor || inquiry.creditor_name || inquiry.inquiry_name || '';
  const defaultResult = {
    linkedAccount: null,
    recommendation: INQUIRY_RECOMMENDATION.NEEDS_REVIEW,
    isCaution: false,
    tag: 'NEEDS REVIEW',
    reason: 'No related opened account found. Review permissible purpose.',
    shouldSkip: false,
    requiresConfirmation: false,
  };

  if (!inquiryCreditor) {
    return { ...defaultResult, reason: 'Creditor name missing. Manual review recommended.' };
  }

  const duplicates = findDuplicateInquiries(inquiry, allInquiries);
  if (duplicates.length > 0) {
    return {
      linkedAccount: null,
      recommendation: INQUIRY_RECOMMENDATION.POSSIBLE_DUPLICATE,
      isCaution: false,
      tag: 'POSSIBLE DUPLICATE',
      reason: `Found ${duplicates.length} similar inquir${duplicates.length === 1 ? 'y' : 'ies'} within 30 days. May be duplicates.`,
      shouldSkip: false,
      requiresConfirmation: false,
      duplicateCount: duplicates.length,
    };
  }

  const link = legitimacyLink;
  if (link) {
    return {
      linkedAccount: link,
      recommendation: INQUIRY_RECOMMENDATION.LIKELY_LEGITIMATE,
      isCaution: true,
      tag: 'LIKELY LEGITIMATE',
      reason: buildLegitimateReason(link),
      shouldSkip: true,
      requiresConfirmation: false,
      accountName: link.account_name || link.creditor_name,
      accountStatus: link.account_status || 'Open',
      accountType: link.account_type || 'Revolving',
      daysDifference: link.days_difference,
    };
  }

  if (!tradelines || tradelines.length === 0) {
    return defaultResult;
  }

  const inquiryBureau = inquiry.bureau || inquiry.bureau_code || '';
  const matchingAccounts = tradelines.filter((account) => {
    const accountName = account.account_name || account.creditor_name || account.creditor || '';
    return creditorNamesMatch(inquiryCreditor, accountName) && accountReportsOnBureau(account, inquiryBureau);
  });

  const closedAccounts = matchingAccounts.filter(isAccountClosed);
  if (closedAccounts.length > 0) {
    const linkedAccount = closedAccounts[0];
    return {
      linkedAccount,
      recommendation: INQUIRY_RECOMMENDATION.NEEDS_REVIEW,
      isCaution: false,
      tag: 'CLOSED ACCOUNT',
      reason: 'Matching account is now closed. Review if inquiry still has permissible purpose.',
      shouldSkip: false,
      requiresConfirmation: false,
      accountName: linkedAccount.account_name || linkedAccount.creditor_name,
      accountStatus: 'Closed',
    };
  }

  if (matchingAccounts.length > 0) {
    return {
      ...defaultResult,
      reason: 'Related account found but not strongly linked to this inquiry. Manual review recommended.',
    };
  }

  return {
    ...defaultResult,
    reason: 'No matching account found. Consider factual/permissible purpose dispute if unauthorized.',
  };
};

export const analyzeInquiries = (inquiries, tradelines = []) => {
  const assignments = buildInquiryLegitimacyAssignments(inquiries, tradelines);
  const results = {
    likelyLegitimate: [],
    needsReview: [],
    possibleDuplicate: [],
    userConfirmation: [],
    all: [],
  };

  for (const inquiry of inquiries) {
    const link = assignments[getInquiryId(inquiry)] || null;
    const analysis = analyzeInquiry(inquiry, tradelines, inquiries, link);
    const enrichedInquiry = { ...inquiry, analysis };
    results.all.push(enrichedInquiry);

    switch (analysis.recommendation) {
      case INQUIRY_RECOMMENDATION.LIKELY_LEGITIMATE:
        results.likelyLegitimate.push(enrichedInquiry);
        break;
      case INQUIRY_RECOMMENDATION.POSSIBLE_DUPLICATE:
        results.possibleDuplicate.push(enrichedInquiry);
        break;
      case INQUIRY_RECOMMENDATION.USER_CONFIRMATION_NEEDED:
        results.userConfirmation.push(enrichedInquiry);
        break;
      default:
        results.needsReview.push(enrichedInquiry);
    }
  }

  return results;
};

export const getInquiryTag = (inquiry, tradelines = [], allInquiries = []) => {
  const assignments = buildInquiryLegitimacyAssignments(allInquiries.length ? allInquiries : [inquiry], tradelines);
  return analyzeInquiry(inquiry, tradelines, allInquiries, assignments[getInquiryId(inquiry)] || null).tag;
};

export const inquiryNeedsCaution = (inquiry, tradelines = [], allInquiries = []) => {
  const assignments = buildInquiryLegitimacyAssignments(allInquiries.length ? allInquiries : [inquiry], tradelines);
  return analyzeInquiry(inquiry, tradelines, allInquiries, assignments[getInquiryId(inquiry)] || null).isCaution;
};

export default {
  analyzeInquiry,
  analyzeInquiries,
  getInquiryTag,
  inquiryNeedsCaution,
  normalizeCreditorName,
  creditorNamesMatch,
  buildInquiryLegitimacyAssignments,
  evaluateInquiryAccountLink,
  formatInquiryDisplayLabel,
  getInquiryId,
  INQUIRY_RECOMMENDATION,
  RECOMMENDATION_LABELS,
  RECOMMENDATION_COLORS,
};
